import { useEffect, useState, type FormEvent } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { APP_NAME } from "@shared/constants";
import { ThemeToggle } from "@client/components/ThemeToggle";
import { useAuth } from "@client/hooks/useAuth";
import { ApiError } from "@client/lib/api";
import { safeNextPath } from "@client/lib/navigation";

const route = getRouteApi("/login");

interface LoginFailure {
  /** "denied" is the allow-list refusal, which is not a credentials problem. */
  kind: "denied" | "credentials" | "unavailable";
  message: string;
}

/**
 * Turns an API error into something worth reading.
 *
 * The distinction that matters is `NOT_ALLOWED_LISTED`: the password was right and
 * saying "wrong password" would send people round a loop resetting a credential
 * that was never the problem. The server writes that sentence — it knows the
 * address to quote — and this only decides how to present it.
 */
function describe(error: unknown): LoginFailure {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "NOT_ALLOWED_LISTED":
        return { kind: "denied", message: error.message };
      case "INVALID_CREDENTIALS":
      case "VALIDATION_FAILED":
        return { kind: "credentials", message: error.message };
      case "RATE_LIMITED":
      case "IDENTITY_PROVIDER_UNAVAILABLE":
        return { kind: "unavailable", message: error.message };
      default:
        return { kind: "unavailable", message: error.message };
    }
  }
  return {
    kind: "unavailable",
    message: "Could not reach the server. Check your connection and try again.",
  };
}

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { next } = route.useSearch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [failure, setFailure] = useState<LoginFailure | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The redirect hangs off the auth state rather than the submit handler, so
  // arriving here with a live session (a bookmarked /login, a second tab) leaves
  // just as promptly as signing in does.
  useEffect(() => {
    if (auth.user) void navigate({ href: safeNextPath(next), replace: true });
  }, [auth.user, next, navigate]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;

    setFailure(null);
    setSubmitting(true);
    try {
      await auth.signIn({ email: email.trim().toLowerCase(), password });
    } catch (error) {
      setFailure(describe(error));
      // Only the password is cleared: retyping an email that was right is busywork.
      setPassword("");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-6 px-4 py-12">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">{APP_NAME}</h1>
          <p className="mt-1 text-sm text-ink-soft">Sign in with your Keesa account.</p>
        </div>
        <ThemeToggle />
      </header>

      <form
        className="island-shell flex flex-col gap-4"
        onSubmit={(event) => void onSubmit(event)}
        noValidate
      >
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-ink" htmlFor="login-email">
            Email
          </label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="rounded-md border border-line bg-chip-bg px-3 py-2 text-sm text-ink"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-ink" htmlFor="login-password">
            Password
          </label>
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="rounded-md border border-line bg-chip-bg px-3 py-2 text-sm text-ink"
          />
        </div>

        {failure && (
          // `alert` rather than `status`: this interrupts, and a screen reader
          // should say so without waiting for the next quiet moment.
          <p
            role="alert"
            className="rounded-md border px-3 py-2 text-sm"
            style={{
              borderColor: failure.kind === "denied" ? "var(--line)" : "var(--chart-crit)",
              color: failure.kind === "denied" ? "var(--ink)" : "var(--chart-crit)",
            }}
          >
            {failure.kind === "denied" && (
              <strong className="block text-xs font-bold uppercase tracking-wider text-ink-soft">
                Access not granted
              </strong>
            )}
            {failure.message}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-azure-deep px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="text-center text-xs text-ink-soft">
        Access is granted per account by an administrator. A valid Keesa login is not, on its own,
        access to this portal.
      </p>
    </main>
  );
}
